/**
 * @module app.shared
 */

import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { convertToString, convertToStringArray } from 'app/shared/lib/utilities';
import { translateModelLabel, translateActionLabel, translateActionItemLabel, translateRouteTitle, translateList } from 'app/shared/lib/i18n';
import { signalNames } from 'app/shared/lib/signals';
import { mapProperties } from 'app/shared/lib/noop';

function getChoiceCount(choices: unknown): number | null {
    if(typeof choices === 'number') {
        return choices;
    }
    else if(Array.isArray(choices)) {
        return choices.length;
    }
    return null;
}

@autoinject()
abstract class i18nValueConverterBase {
    public signals: string[] = [signalNames.languageChanged];
    constructor(protected readonly i18n: I18N) { }
}

export class WhatValueConverter extends i18nValueConverterBase {
    public toView(key: unknown, whatKey?: unknown, count?: string | number): string {
        return translateActionLabel(this.i18n, convertToString(key), convertToString(whatKey) || null, count);
    }
}

/**
 * Used to help translate an Action label with `{{what}}` and `{{item}}` interpolation
 * @param key action key for translation
 * @param whatKey model key to be translated for interpolation of the `{{what}}`
 * @param itemValue a string to be interpolated into the `{{item}}`
*/
export class WhatItemValueConverter extends i18nValueConverterBase {
    public toView(key: unknown, whatKey: unknown, itemValue: string): string {
        return translateActionItemLabel(this.i18n, convertToString(key), convertToString(whatKey), itemValue);
    }
}

export class SelectOneValueConverter extends i18nValueConverterBase {
    public toView(choices: unknown, whatKey?: unknown, parentKey?: unknown): string {
        const count = getChoiceCount(choices);
        if(count === null && parentKey) {
            // Select 'Parent' First
            return translateActionLabel(this.i18n, 'ui:placeholders.select.first', convertToString(parentKey), 1);
        }
        if(count === 0 || count === null) {
            // No 'Choices'
            return translateActionLabel(this.i18n, 'ui:placeholders.select.none', convertToString(whatKey), 0);
        }
        return translateActionLabel(this.i18n, 'ui:placeholders.select.item', convertToString(whatKey), 1);
    }
}

export class SelectMultipleValueConverter extends i18nValueConverterBase {
    public toView(choices: unknown, whatKey?: unknown, parentKey?: unknown): string {
        const count = getChoiceCount(choices);
        if(count === null && parentKey) {
            // Select 'Parent' First
            return translateActionLabel(this.i18n, 'ui:placeholders.select.first', convertToString(parentKey), 1);
        }
        if(count === 0 || count === null) {
            // No 'Choices'
            return translateActionLabel(this.i18n, 'ui:placeholders.select.none', convertToString(whatKey), 0);
        }
        return translateActionLabel(this.i18n, 'ui:placeholders.select.item', convertToString(whatKey), 'multiple');
    }
}

export class LabelValueConverter extends i18nValueConverterBase {
    public toView(key: unknown, count?: string | number): string {
        return translateModelLabel(this.i18n, convertToString(key), count);
    }
}

export class RouteTitleValueConverter extends i18nValueConverterBase {
    public toView(title: unknown): string {
        return translateRouteTitle(this.i18n, convertToString(title) || null);
    }
}

export class ListTextValueConverter extends i18nValueConverterBase {
    public toView(values: unknown, conjunction: unknown): string | null {
        if(!values) { return null; }

        const array: string[] = convertToStringArray(values);
        if(array.length == 0) { return null; }
        if(array.length == 1) { return array[0]; }

        return translateList(this.i18n, array, convertToString(conjunction) || undefined);
    }
}

/**
 * Translates both the key and the passed options.
 * @param {Record<string, string>} [options = {}] A dictionary of parameterized keys.
 * @info The i18next parameterization feature is generally for interpolating
 *  data values. However, an application of our size typically reuses similar
 *  translations to keep the translations DRY. That means that we will often
 *  interpolate translations into other translations. This value converter
 *  assists with that process.
 * @example ${'ui:labels.only.contracted' | tr: { what: 'cd:provider.$_plural' } }"
 */
export class TrValueConverter extends i18nValueConverterBase {
    toView(key: string, options: Record<string, string> = {}) {
        return this.i18n.tr(key, mapProperties(options, (key) => this.i18n.tr(key)));
    }
}

export class TNestValueConverter {
    public toView(key: unknown): string | null {
        if(!key) { return null; }
        return `$t(${key})`;
    }
}
